import { Logger } from '@packages/shared/core/common/logger/interfaces';
import { CookiesService } from '@packages/shared/core/common/services';
import { CustomAppEvents } from '@packages/shared/core/common/entities';
import { checkIsClientSide } from '@packages/shared/core/common/utils/checkIsClientSide';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { ONE_TRUST_COOKIE_CONSENT_COOKIE } from '../constants';
import { CookieConsentCallback, CookieConsentState, CookieConsentType } from '../entities';
import { CookieConsentService } from '../interfaces';

export class OneTrustCookieConsentService implements CookieConsentService {
  private readonly cookiesService: CookiesService;
  private readonly logger: Logger;
  private consentChangeListeners: Array<CookieConsentCallback> = [];
  private consentLoadListeners: Array<CookieConsentCallback> = [];
  private initialized = false;

  constructor(cookiesService: CookiesService, logger: Logger) {
    this.cookiesService = cookiesService;
    this.logger = logger;
  }

  initialize(): void {
    if (this.consentLibrary) {
      this.setupListeners();
      return;
    }

    document.addEventListener(
      CustomAppEvents.ONE_TRUST_LOADED,
      () => {
        this.setupListeners();
      },
      { once: true },
    );
  }

  toggleReconsent(): void {
    if (!this.consentLibrary) return;

    this.consentLibrary.ToggleInfoDisplay();
  }

  addConsentChangeListener(callback: CookieConsentCallback) {
    this.consentChangeListeners.push(callback);
  }

  removeConsentChangeListener(callback: CookieConsentCallback) {
    const index = this.consentChangeListeners.indexOf(callback);
    if (index > -1) {
      this.consentChangeListeners.splice(index, 1);
    }
  }

  addConsentLoadListener(callback: CookieConsentCallback) {
    this.consentLoadListeners.push(callback);
  }

  removeConsentLoadListener(callback: CookieConsentCallback) {
    const index = this.consentLoadListeners.indexOf(callback);
    if (index > -1) {
      this.consentLoadListeners.splice(index, 1);
    }
  }

  isStrictlyNecessaryConsentGiven() {
    return this.isConsentGroupActive(CookieConsentType.STRICTLY_NECESSARY) || true;
  }

  isPerformanceConsentGiven() {
    return this.isConsentGroupActive(CookieConsentType.PERFORMANCE);
  }

  isFunctionalConsentGiven() {
    return this.isConsentGroupActive(CookieConsentType.FUNCTIONAL);
  }

  isTargetingConsentGiven() {
    return this.isConsentGroupActive(CookieConsentType.TARGETING);
  }

  isInitialized() {
    return this.initialized;
  }

  private get consentLibrary() {
    if (!checkIsClientSide()) {
      return;
    }

    return window.OneTrust;
  }

  private notifyConsentLoadListeners() {
    const consentState = this.getCookieConsentState();

    this.consentLoadListeners.forEach((listener) => {
      try {
        listener(consentState);
      } catch (err) {
        const error = normalizeError(err);
        this.logger.error('Failed to execute consent load listener', {
          error: error.message,
        });
      }
    });
  }

  private notifyConsentChangeListeners() {
    const consentState = this.getCookieConsentState();

    this.consentChangeListeners.forEach((listener) => {
      try {
        listener(consentState);
      } catch (err) {
        const error = normalizeError(err);
        this.logger.error('Failed to execute consent change listener', {
          error: error.message,
        });
      }
    });
  }

  private setupListeners() {
    if (!this.consentLibrary) {
      return;
    }

    if (this.isInitialized()) {
      return;
    }
    this.initialized = true;
    this.notifyConsentLoadListeners();

    this.consentLibrary.OnConsentChanged(() => this.notifyConsentChangeListeners());
  }

  private getConsentGroupsFromWindow() {
    if (!checkIsClientSide()) {
      return '';
    }

    const groups = window.OptanonActiveGroups || '';

    // Trim leading and trailing commas
    return groups.replace(/^,|,$/g, '');
  }

  private getConsentGroupsFromCookies() {
    if (!checkIsClientSide()) {
      return '';
    }

    const consentCookie = this.cookiesService.get(ONE_TRUST_COOKIE_CONSENT_COOKIE);

    if (!consentCookie) {
      return '';
    }

    const groupsMatch = consentCookie.match(/groups=([^;]*)/);

    if (!groupsMatch || groupsMatch.length === 0) {
      return '';
    }

    const groupsString = decodeURIComponent(groupsMatch[1]);
    return groupsString
      .split(',')
      .filter((group) => group.endsWith(':1'))
      .map((group) => group.split(':')[0])
      .join(',');
  }

  private get consentGroups() {
    if (!checkIsClientSide()) {
      return '';
    }

    const windowGroups = this.getConsentGroupsFromWindow();

    if (windowGroups) {
      return windowGroups;
    }

    const cookieGroups = this.getConsentGroupsFromCookies();

    if (cookieGroups) {
      return cookieGroups;
    }

    return '';
  }

  private isConsentGroupActive(groupId: CookieConsentType) {
    const activeGroups = this.consentGroups;
    return activeGroups.includes(groupId);
  }

  private getCookieConsentState(): CookieConsentState {
    return Object.keys(CookieConsentType).reduce((state, key) => {
      const name = key as keyof typeof CookieConsentType;
      const groupId = CookieConsentType[name];
      state[name] = this.isConsentGroupActive(groupId);
      return state;
    }, {} as CookieConsentState);
  }
}
